/**
 * Radio — controlled radio button with label support.
 */

import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  description?: string;
  wrapperClassName?: string;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ label, description, className, wrapperClassName, id, ...props }, ref) => {
    const radioId = id || props.name;

    return (
      <div className={cn('flex items-start gap-2.5', wrapperClassName)}>
        <input
          ref={ref}
          type="radio"
          id={radioId}
          className={cn(
            'mt-0.5 h-4 w-4 border border-input bg-surface',
            'transition-all duration-fast ease-standard',
            'hover:border-neutral-400',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/15 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'checked:bg-primary-700 checked:border-primary-700',
            'disabled:cursor-not-allowed disabled:opacity-40',
            className,
          )}
          {...props}
        />
        {label && (
          <div className="flex flex-col gap-0.5">
            <label htmlFor={radioId} className="text-sm font-medium text-foreground cursor-pointer tracking-tight">
              {label}
            </label>
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
          </div>
        )}
      </div>
    );
  },
);

Radio.displayName = 'Radio';
